import { Picture } from "@/type/core/offices/offices.types";
import imageECMA from "@static/core/img/logo_ecma.png";
import imageUNIVERSIGN from "@static/core/img/utc_signature.png";
import imageJSE from "@static/core/img/jse_transparent.png";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import {
  File,
  Signatory,
  Signature,
} from "@/type/core/dashboard/dashboard.types";

import {
  addTransactionSignature,
  MoveSignature,
} from "@state/transactions/CurrentTransactionEvents";
import { MAX_SIGNATURE_AMOUNT } from "@utils/Constant";

interface SignaturePictureParams {
  signatorySignaturePictures: Picture[];
  // office or sealing pictures can be signaturePictureDefault or scellementPicture from the office
  officeSignatureOrSealingPictures: Picture[];
  rank: number;
  isUserInOffice: boolean;
  transactionType: TransactionType;
  pattern?: SignaturePatternEnum;
  priority?: SignaturePicturePriorityEnum;
  image?: string;
}

class SignatureHelper {
  private static _getPreloadedImage = (
    isUserInOffice: boolean,
    transactionType: TransactionType,
    pattern?: SignaturePatternEnum,
    priority?: SignaturePicturePriorityEnum,
  ): string => {
    if (transactionType === "scellement") {
      return imageECMA;
    } else if (
      !isUserInOffice &&
      pattern === SignaturePatternEnum.SIGN_LEVEL_2_EC
    ) {
      return imageJSE;
    } else if (pattern === SignaturePatternEnum.DEFAULT) {
      return imageUNIVERSIGN;
    } else if (
      isUserInOffice &&
      priority === SignaturePicturePriorityEnum.OFFICE_PICTURE
    ) {
      return imageECMA;
    } else {
      return imageUNIVERSIGN;
    }
  };

  static getCorrectSignaturePicture(
    signaturePictureParams: SignaturePictureParams,
  ): string | undefined {
    const {
      signatorySignaturePictures,
      // office or sealing pictures can be signaturePictureDefault or scellementPicture from the office
      officeSignatureOrSealingPictures,
      rank,
      isUserInOffice,
      transactionType,
      pattern,
      priority,
      image,
    } = signaturePictureParams;

    const defaultImage =
      image ??
      this._getPreloadedImage(
        isUserInOffice,
        transactionType,
        pattern,
        priority,
      );

    if (pattern === SignaturePatternEnum.DEFAULT) {
      return defaultImage;
    }

    switch (priority) {
      case SignaturePicturePriorityEnum.NO_PICTURE:
        return "";
      case SignaturePicturePriorityEnum.GUEST_PICTURE:
        return defaultImage;
      case SignaturePicturePriorityEnum.LIBELLE_PICTURE:
      case SignaturePicturePriorityEnum.MANUSCRIT_PICTURE:
      case SignaturePicturePriorityEnum.PERSONAL_PICTURE:
        return (
          signatorySignaturePictures.find((picture) => picture.rank == rank)
            ?.picture ?? ""
        );
      case SignaturePicturePriorityEnum.OFFICE_PICTURE:
      case SignaturePicturePriorityEnum.SCELLEMENT_PICTURE:
        return (
          officeSignatureOrSealingPictures.find(
            (picture) => picture.rank == rank,
          )?.picture ?? defaultImage
        );
      case undefined:
      default:
        return defaultImage;
    }
  }

  static computePattern = (
    signaturePriorityRule?: SignaturePicturePriorityEnum,
    email?: string,
    pattern?: SignaturePatternEnum,
  ): SignaturePatternEnum | undefined => {
    if (signaturePriorityRule === SignaturePicturePriorityEnum.NO_PICTURE) {
      if (email === "scellement") {
        return SignaturePatternEnum.SEAL_WITHOUT_LOGO;
      } else {
        return SignaturePatternEnum.SIGN_WITHOUT_LOGO;
      }
    } else if (
      signaturePriorityRule === SignaturePicturePriorityEnum.SCELLEMENT_PICTURE
    ) {
      return SignaturePatternEnum.SEAL;
    }
    return pattern ?? SignaturePatternEnum.SIGN_LEVEL_2_EC;
  };

  static getSignaturePatternFromEmail(
    signatures: Signature[],
    email: string,
  ): SignaturePatternEnum | undefined {
    const signature = signatures.find((signature) => signature.email === email);
    if (signature) {
      return signature.pattern;
    }
    return undefined;
  }

  static buildSignatureIdentificator = (
    pdfIndex: number,
    indexSignatory: number,
    indexSignature: number,
  ): string => {
    return `pdf_${pdfIndex}_signatory_${indexSignatory}_signature_${indexSignature}`;
  };

  static getFileIndexFromIdentificator = (identificator: string): number => {
    const indexInString = identificator.split("_").at(1) ?? "-1";
    return +indexInString;
  };

  static getSignatoryIndexFromIdentificator = (
    identificator: string,
  ): number => {
    const indexInString = identificator.split("_").at(3) ?? "-1";
    return +indexInString;
  };

  static getSignatureIndexFromIdentificator = (
    identificator: string,
  ): number => {
    const indexInString = identificator.split("_").pop() ?? "-1";
    return +indexInString;
  };

  static getMovedSignaturesOfOneSignatoryOnOneFile = (
    signatures: Signature[],
    signatory: Signatory,
    fileIndex: number,
  ): Signature[] => {
    return signatures.filter((signature) => {
      return (
        signature.isMoved &&
        signature.rank === fileIndex &&
        signature.email === signatory.email
      );
    });
  };

  static getAllSignaturesOfOneSignatoryOnOneFile = (
    signatures: Signature[],
    signatory: Signatory,
    fileIndex: number,
  ): Signature[] => {
    return signatures.filter((signature) => {
      return (
        signature.rank === fileIndex && signature.email === signatory.email
      );
    });
  };

  static getSignatureAmountForOneSignatoryOfOneFile = (
    signatures: Signature[],
    signatory: Signatory,
    fileIndex: number,
  ): number => {
    return SignatureHelper.getMovedSignaturesOfOneSignatoryOnOneFile(
      signatures,
      signatory,
      fileIndex,
    ).length;
  };

  static areSignatureMultiple = (signatures: Signature[]): boolean => {
    if (signatures.length < 2 || signatures[0].email === "scellement") {
      return false;
    }

    const seenEmails = new Set<string | null | undefined>();

    for (const signature of signatures) {
      if (seenEmails.has(signature.email)) {
        return true;
      }
      seenEmails.add(signature.email);
    }

    return false;
  };

  static shouldAddOrDeleteASignature = (
    signatureBeforeMove: MoveSignature,
    signatureAfterMove: MoveSignature,
    signatures: Signature[],
    signatoryIndex: number,
    signatory: Signatory,
  ): Signature[] => {
    if (signatureBeforeMove.isMoved === signatureAfterMove.isMoved) {
      // No changes
      return signatures;
    }
    if (signatureAfterMove.email === "scellement") return signatures;
    if (signatureAfterMove.isMoved) {
      // add
      const newEmptySignature = {
        ...signatureAfterMove,
      };
      newEmptySignature.isMoved = false;
      newEmptySignature.positionX = 0;
      newEmptySignature.positionY = 0;

      let identificator = "";
      if (signatureAfterMove && signatureAfterMove.rank) {
        identificator = SignatureHelper.buildSignatureIdentificator(
          signatureAfterMove.rank - 1,
          signatoryIndex,
          SignatureHelper.getSignatureIndexFromIdentificator(
            signatureAfterMove.identificator ?? "",
          ) + 1,
        );
      }

      newEmptySignature.identificator = identificator;

      return [...signatures, newEmptySignature];
    } else {
      // delete
      const signaturesOfSignatoryOnFile =
        SignatureHelper.getAllSignaturesOfOneSignatoryOnOneFile(
          signatures,
          signatory,
          signatureAfterMove.rank as number,
        );

      const lastMovedSignature = signaturesOfSignatoryOnFile.at(
        signaturesOfSignatoryOnFile.length - 2,
      );

      const lastSignature = signaturesOfSignatoryOnFile.at(
        signaturesOfSignatoryOnFile.length - 1,
      );

      // we overwrite the signature to be deleted by the last moved signature, and we empty the last moved signature
      signatures = signatures.map((signature) => {
        if (signature.identificator === lastMovedSignature?.identificator) {
          // we empty the last moved but keep the identificator
          signature = {
            ...lastSignature,
            identificator: lastMovedSignature?.identificator,
          };
        } else if (
          signature.identificator === signatureAfterMove.identificator
        ) {
          // we take the data of the lastMoved and overwrite the signature to be deleted
          signature = {
            ...lastMovedSignature,
            identificator: signature.identificator,
          };
        }
        return signature;
      });

      // we delete the last signature
      return signatures.filter((signature) => {
        return signature.identificator !== lastSignature?.identificator;
      });
    }
  };

  static shouldSignatureBeDisabled = (
    signatures: Signature[],
    signatory: Signatory,
    signatories: Signatory[],
    fileIndex: number,
  ): boolean => {
    const numberOfSignatoriesToSign = signatories.filter((signatory) => {
      return (
        SignatureHelper.getSignatureAmountForOneSignatoryOfOneFile(
          signatures,
          signatory,
          fileIndex,
        ) === 0
      );
    }).length;
    const numberOfSignatureOnThisFile = signatures.filter(
      (signature) => signature.rank === fileIndex && signature.isMoved,
    ).length;
    const signatoryHasSignedAtLeastOnce =
      SignatureHelper.getSignatureAmountForOneSignatoryOfOneFile(
        signatures,
        signatory,
        fileIndex,
      ) >= 1;
    return (
      numberOfSignatoriesToSign + numberOfSignatureOnThisFile ===
        MAX_SIGNATURE_AMOUNT && signatoryHasSignedAtLeastOnce
    );
  };

  static shouldSignaturesBeDisabled = (
    signatures: Signature[],
    fileIndex: number,
  ): boolean => {
    const numberOfSignatureOnThisFile = signatures.filter(
      (signature) => signature.rank === fileIndex && signature.isMoved,
    ).length;
    return numberOfSignatureOnThisFile === MAX_SIGNATURE_AMOUNT;
  };

  static didSignatoriesSignAtLeastOnceThisFile = (
    signatures: Signature[],
    signatories: Signatory[],
    fileIndex: number,
  ): boolean => {
    for (const signatory of signatories) {
      const signatoryDidNotSign =
        SignatureHelper.getSignatureAmountForOneSignatoryOfOneFile(
          signatures,
          signatory,
          fileIndex,
        ) === 0;
      if (signatoryDidNotSign) return false;
    }
    return true;
  };

  static didSignatoriesSignAtLeastOnceOnEveryFile = (
    signatures: Signature[],
    signatories: Signatory[],
    files: File[],
  ): boolean => {
    for (let fileIndex = 1; fileIndex <= files.length; fileIndex++) {
      if (
        !SignatureHelper.didSignatoriesSignAtLeastOnceThisFile(
          signatures,
          signatories,
          fileIndex,
        )
      ) {
        return false;
      }
    }
    return true;
  };

  static createPairOfSignature = (
    isFirstSignatureMoved: boolean,
    newSignature: Signature,
    fileIndex: number,
    indexSignatory: number,
  ): void => {
    if (isFirstSignatureMoved) {
      addTransactionSignature(newSignature);
      addTransactionSignature({
        ...newSignature,
        identificator: SignatureHelper.buildSignatureIdentificator(
          fileIndex,
          indexSignatory,
          1,
        ),
        isMoved: false,
        page: 0,
        positionX: 0,
        positionY: 0,
      });
    } else {
      addTransactionSignature(newSignature);
    }
  };
}

export default SignatureHelper;
